import { Link } from 'react-router-dom'
import { LineChart, Mail, Store } from 'lucide-react'

type SupportActionId = 'contact' | 'retailer' | 'investor'

interface SupportAction {
	id: SupportActionId
	desktopTitle: string
	desktopDescription: string
	mobileTitle: string
	variant: 'orange' | 'green' | 'yellow'
	isLink?: boolean
}

const SUPPORT_ACTIONS: SupportAction[] = [
	{
		id: 'contact',
		desktopTitle: 'Contact',
		desktopDescription: 'Talk to our team about support and enquiries',
		mobileTitle: 'Contact',
		variant: 'yellow',
		isLink: true,
	},
	{
		id: 'retailer',
		desktopTitle: 'Interested in Distributing?',
		desktopDescription: 'Stock Kibu in your store',
		mobileTitle: 'Become a Retailer',
		variant: 'orange',
	},
	{
		id: 'investor',
		desktopTitle: 'Investor Enquiries',
		desktopDescription: 'Partner with us on our mission',
		mobileTitle: 'Investor Enquiries',
		variant: 'green',
	},
]

const supportActionIcons = {
	contact: Mail,
	retailer: Store,
	investor: LineChart,
} as const

const desktopVariantClasses = {
	orange: {
		card: 'bg-kibuBlue md:hover:bg-kibuBlue/90',
		title: 'text-white',
		icon: 'text-white',
	},
	green: {
		card: 'bg-green md:hover:bg-green/90',
		title: 'text-white',
		icon: 'text-white',
	},
	yellow: {
		card: 'bg-gray-100 md:hover:bg-grey-100/90',
		title: 'text-black',
		icon: 'text-black',
	},
} as const

const mobileVariantClasses = {
	orange: 'bg-orange/15 text-orange active:bg-orange/25',
	green: 'bg-green/15 text-green active:bg-green/25',
	yellow: 'bg-specialYellow/30 text-[#6A4700] active:bg-specialYellow/40',
} as const

interface NavSupportActionsProps {
	variant: 'desktop' | 'mobile'
	contactUrl: string
	onContactClick: () => void
	onRetailerClick: () => void
	onInvestorClick: () => void
}

const NavSupportActions = ({
	variant,
	contactUrl,
	onContactClick,
	onRetailerClick,
	onInvestorClick,
}: NavSupportActionsProps) => {
	const getAction = (id: SupportActionId) => SUPPORT_ACTIONS.find((action) => action.id === id)

	const contactAction = getAction('contact')
	const retailerAction = getAction('retailer')
	const investorAction = getAction('investor')

	const renderDesktopLinkAction = (action: SupportAction, to: string, onClick: () => void) => {
		const themeClasses = desktopVariantClasses[action.variant]
		const Icon = supportActionIcons[action.id]
		const sharedClassName = `relative overflow-hidden px-6 py-5 rounded-2xl transition-colors duration-300 group ${themeClasses.card}`

		return (
			<Link key={action.id} to={to} className={`block ${sharedClassName}`} onClick={onClick}>
				<div className="pr-12 pb-10">
					<h3 className={`font-headline text-xl mb-1.5 md:group-hover:scale-[102%] transition-transform duration-300 ${themeClasses.title}`}>
						{action.desktopTitle}
					</h3>
					<p className="text-sm text-gray-600 leading-relaxed">{action.desktopDescription}</p>
				</div>
				<Icon className={`size-8 absolute right-6 bottom-5 opacity-85 md:group-hover:scale-110 transition-transform duration-300 ${themeClasses.icon}`} />
			</Link>
		)
	}

	const renderDesktopButtonAction = (action: SupportAction, onClick: () => void) => {
		const themeClasses = desktopVariantClasses[action.variant]
		const Icon = supportActionIcons[action.id]
		const sharedClassName = `relative overflow-hidden px-6 py-5 rounded-2xl transition-colors duration-300 group ${themeClasses.card}`

		return (
			<button
				key={action.id}
				onClick={(event) => {
					event.stopPropagation()
					onClick()
				}}
				className={`w-full text-left ${sharedClassName}`}
			>
				<div className="pr-12">
					<h3 className={`font-headline text-xl mb-1.5 md:group-hover:scale-[102%] transition-transform duration-300 ${themeClasses.title}`}>
						{action.desktopTitle}
					</h3>
					<p className="text-sm text-gray-200 leading-relaxed">{action.desktopDescription}</p>
				</div>
				<Icon className={`size-8 absolute right-6 top-1/2 -translate-y-1/2 opacity-85 md:group-hover:scale-110 transition-transform duration-300 ${themeClasses.icon}`} />
			</button>
		)
	}

	if (variant === 'desktop') {
		return (
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{contactAction && renderDesktopLinkAction(contactAction, contactUrl, onContactClick)}

				<div className="flex flex-col gap-4">
					{retailerAction && renderDesktopButtonAction(retailerAction, onRetailerClick)}
					{investorAction && renderDesktopButtonAction(investorAction, onInvestorClick)}
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-3">
			{SUPPORT_ACTIONS.map((action) => {
				const Icon = supportActionIcons[action.id]
				const sharedClassName = `flex items-center justify-between rounded-2xl px-6 py-5 font-headline text-xl transition-colors  transition-colors bg-grey text-gray-700 active:bg-gray-200}`

				if (action.isLink) {
					return (
						<Link key={action.id} to={contactUrl} className={sharedClassName} onClick={onContactClick}>
							<span>{action.mobileTitle}</span>
							<Icon className="w-8 h-8 flex-shrink-0 opacity-85" />
						</Link>
					)
				}

				const clickHandler = action.id === 'retailer' ? onRetailerClick : onInvestorClick
				return (
					<button key={action.id} onClick={clickHandler} className={`w-full text-left ${sharedClassName}`}>
						<span>{action.mobileTitle}</span>
						<Icon className="w-8 h-8 flex-shrink-0 opacity-85" />
					</button>
				)
			})}
		</div>
	)
}

export default NavSupportActions
